import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  getMultiFactorResolver,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  TotpMultiFactorGenerator,
  type MultiFactorError,
  type User,
} from "firebase/auth";
import { Navigate, useLocation } from "react-router";
import {
  setApiErrorListener,
  setAuthTokenProvider,
} from "../../../shared/api-client";
import { identifyAnalyticsUser, resetAnalytics } from "./analytics";
import { auth, firebaseConfigured } from "./firebase";
import styles from "./auth.module.css";

/**
 * What a password got. `needs_code` is an account with an authenticator
 * app: the password was right, and the sign-in finishes on the code.
 */
export type SignInResult =
  | { status: "signed_in" }
  | { status: "needs_code"; verify(code: string): Promise<void> };

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  configured: boolean;
  signIn(email: string, password: string): Promise<SignInResult>;
  signOut(): Promise<void>;
  /**
   * The API refused this session for want of an authenticator code: the
   * Company requires one and this sign-in never gave it. The app shell
   * shows the setup screen instead of the page while it is true.
   */
  twoFactorRequired: boolean;
}

/** Who is signed in, as every screen sees them. */
export type AccountUser = User;

/** The server's code for a session its Company's two-factor switch refuses. */
export const TWO_FACTOR_REQUIRED = "two_factor_required";

const AuthContext = createContext<AuthContextValue | null>(null);

function isMultiFactorError(err: unknown): err is MultiFactorError {
  return (
    typeof err === "object" &&
    err !== null &&
    "code" in err &&
    err.code === "auth/multi-factor-auth-required"
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(
    () => auth?.currentUser ?? null,
  );
  const [loading, setLoading] = useState(firebaseConfigured);
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);

  useEffect(() => {
    if (!auth) {
      setAuthTokenProvider(null);
      setLoading(false);
      return undefined;
    }

    const authInstance = auth;
    setAuthTokenProvider(
      async () => authInstance.currentUser?.getIdToken() ?? null,
    );
    setApiErrorListener((err) => {
      if (err.code === TWO_FACTOR_REQUIRED) setTwoFactorRequired(true);
    });
    const unsubscribe = onAuthStateChanged(authInstance, (nextUser) => {
      setUser(nextUser);
      if (!nextUser) setTwoFactorRequired(false);
      setLoading(false);
    });
    return () => {
      unsubscribe();
      setAuthTokenProvider(null);
      setApiErrorListener(null);
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      configured: firebaseConfigured,
      twoFactorRequired,
      async signIn(email, password) {
        if (!auth) throw new Error("Firebase Auth is not configured");
        const authInstance = auth;
        try {
          await signInWithEmailAndPassword(authInstance, email, password);
          return { status: "signed_in" };
        } catch (err) {
          if (!isMultiFactorError(err)) throw err;
          const resolver = getMultiFactorResolver(authInstance, err);
          const hint = resolver.hints.find(
            (h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID,
          );
          // Only an authenticator app is ever enrolled here; an account
          // with some other factor has nowhere on this page to give it.
          if (!hint) throw err;
          return {
            status: "needs_code",
            async verify(code: string) {
              await resolver.resolveSignIn(
                TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code),
              );
            },
          };
        }
      },
      async signOut() {
        if (!auth) return;
        await firebaseSignOut(auth);
        resetAnalytics();
      },
    }),
    [loading, twoFactorRequired, user],
  );

  // Labels analytics with who is signed in and their Company. Keyed on
  // the uid, so a token refresh is not a second identify.
  const uid = user?.uid;
  const email = user?.email ?? null;
  useEffect(() => {
    if (!uid) return undefined;
    const controller = new AbortController();
    void identifyAnalyticsUser(uid, email, controller.signal);
    return () => controller.abort();
  }, [uid, email]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}

export function useOptionalAuth() {
  return useContext(AuthContext);
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className={styles.loadingPage}>
        <div className={styles.loading}>Loading</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <>{children}</>;
}
